import { isEmail, normEmail, passwordProblems, type PasswordProblem } from '@zenit/contracts';

// The league's own login screens (22 Oct 2026; not in the prototype, drawn in its "Portal Zenit+" / "Acceso" style).
export type AuthScreen = 'acceso' | 'registro' | 'revisa' | 'verificar' | 'recuperar' | 'contrasena' | 'admin';
export interface AuthRoute { screen: AuthScreen; token?: string; email?: string }

const PATHS: Record<string, AuthScreen> = { '/acceso': 'acceso', '/registro': 'registro', '/verificar': 'verificar', '/recuperar': 'recuperar', '/contrasena': 'contrasena', '/admin': 'admin' };
export const AUTH_PATH: Record<AuthScreen, string> = { acceso: '/acceso', registro: '/registro', revisa: '/acceso', verificar: '/verificar', recuperar: '/recuperar', contrasena: '/contrasena', admin: '/admin' };

/** The auth screen a URL opens, or null for the league's own screens. Email links carry their token in `?token=`. */
export function authRoute(path: string, search: string): AuthRoute | null {
  const screen = PATHS[path]; if (!screen) return null;
  const token = new URLSearchParams(search).get('token');
  return token && (screen === 'verificar' || screen === 'contrasena') ? { screen, token } : { screen };
}

/** Where an account goes once signed in. */
export const afterSignIn = (role: 'participant' | 'admin'): string => (role === 'admin' ? '/admin' : '/cartera');

export const RULES: { key: PasswordProblem; label: string }[] = [
  { key: 'length', label: '12 caracteres o más' }, { key: 'upper', label: 'Una mayúscula' }, { key: 'lower', label: 'Una minúscula' },
  { key: 'digit', label: 'Un número' }, { key: 'symbol', label: 'Un símbolo' },
];
export const ruleTicks = (pw: string) => { const p = passwordProblems(pw); return RULES.map(r => ({ label: r.label, ok: !p.includes(r.key) })); };

export interface RegistroForm { nombre: string; apellido: string; email: string; password: string; password2: string }
export function registroVals(f: RegistroForm) {
  const strong = passwordProblems(f.password).length === 0, same = f.password === f.password2;
  return {
    rules: ruleTicks(f.password),
    mismatch: f.password2 && !same ? 'Las contraseñas no coinciden.' : '',
    canSubmit: !!f.nombre.trim() && !!f.apellido.trim() && isEmail(normEmail(f.email)) && strong && same,
  };
}

const MESSAGES: Record<string, string> = {
  credentials: 'Correo o contraseña incorrectos.',
  not_member: 'Este correo no pertenece a ningún alumno de Zenit+. Usa el correo con el que te registraste en Zenit+.',
  signup_closed: 'El registro en la Liga todavía no está abierto.',
  membership_unavailable: 'No podemos comprobar tu correo de Zenit+ ahora mismo. Inténtalo de nuevo en unos minutos.',
  rate_limited: 'Demasiados intentos. Espera unos minutos y vuelve a intentarlo.',
  token: 'Este enlace ha caducado o ya se ha usado.',
  invalid: 'Revisa los datos: hay un campo que no es válido.',
  network: 'No hemos podido conectar. Inténtalo de nuevo.',
};
/** The Spanish message for an API error code (anything unexpected: the connection message). */
export const messageFor = (code: string): string => MESSAGES[code] ?? MESSAGES.network;
